// src/components/tokenPage/overview/RiskEfficiencyCard.tsx

import React, { useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { MetricBox } from "../shared/TokenDashboardComponents";
import { DataCard } from "@/components/ui";
import { useRiskMetrics } from "@/hooks/metrics";

type TimeWindow = '30d' | '90d' | '180d' | '365d';

const WINDOW_DAYS: Record<TimeWindow, number> = {
  '30d': 30,
  '90d': 90,
  '180d': 180,
  '365d': 365,
};

const RiskEfficiencyCard: React.FC = () => {
  const [riskWindow, setRiskWindow] = useState<TimeWindow>('90d');
  
  const windowDays = WINDOW_DAYS[riskWindow];
  const {
    volatility,
    sharpe,
    sortino,
    maxDrawdown,
    isLoading,
  } = useRiskMetrics(windowDays);

  // Hide if no data available (after loading)
  const hasData = volatility != null || sharpe != null || sortino != null || maxDrawdown != null;
  if (!isLoading && !hasData) {
    return null;
  }

  // Format volatility and drawdown as percentages for display
  const volPct = volatility != null ? volatility * 100 : null;
  const ddPct = maxDrawdown != null ? maxDrawdown * 100 : null;

  return (
    <DataCard
      title="Risk & Efficiency"
      subtitle="Vol, Sharpe, Sortino, Drawdown"
      icon="gauge"
      iconColor="text-indigo-400"
      isLoading={isLoading}
      loadingVariant="stats"
      actions={
        <TimeRangePicker 
          options={['30d', '90d', '180d', '365d'] as const} 
          value={riskWindow} 
          onChange={setRiskWindow} 
          theme="blue" 
        />
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <MetricBox label="Volatility" value={volPct} fmt="pct1" />
        <MetricBox label="Max Drawdown" value={ddPct} fmt="pct1" />
        <MetricBox label="Sharpe" value={sharpe} fmt="ratio2" toneBySign />
        <MetricBox label="Sortino" value={sortino} fmt="ratio2" toneBySign />
      </div>
    </DataCard>
  );
};

export default RiskEfficiencyCard;
